import React, { useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";
import { EMAIL_PATTERN } from "./nativeInvoiceFormHelpers";
import { formatInvoicePayUrlDisplay } from "./resolveNativeInvoiceEmailPayUrl";
import styles from "./EmailPreview.module.scss";

const wrapEmail = (email: string): string => `<${email}>`;

// The card lays out at this fixed width and is scaled down uniformly (like the
// invoice paper) so the whole email always fits the pane without reflowing.
// Deliberately narrow (portrait-ish) to match the final email format — the
// tighter column also keeps the type larger for a given pane width. Must stay
// in sync with .emailPreviewCard's width in EmailPreview.module.scss.
const CARD_NATURAL_WIDTH_PX = 567;

interface Props {
    fromName: string;
    fromEmail: string;
    replyToName: string;
    replyToEmail: string;
    customerName: string;
    toEmails: string[];
    ccEmails: string[];
    bccEmails: string[];
    emailSubject: string;
    emailMessage: string;
    // Present when payment collection is on. Same Kick /pay/{token} URL the
    // sent email embeds — never a checkout.stripe.com URL.
    payUrl?: string | null;
    attachPdf: boolean;
    invoiceNumber: string;
    // Live miniature of the invoice document, rendered inside the attachment
    // card (the flight animation from the details step lands on it).
    invoiceThumbnail: React.ReactNode;
    onDownloadInvoice: () => void;
    isDownloading: boolean;
    // Header strip (the Preview mode toggle bar) rendered attached to the top
    // of the email card, matching the card's scaled width.
    header?: React.ReactNode;
}

// Mirrors the header strip's rendered height (28px controls + 2×10px
// padding), reserved out of the viewport before fitting the card.
const HEADER_BAR_HEIGHT_PX = 48;

export const EmailPreview: React.FC<Props> = ({
    fromName,
    fromEmail,
    replyToName,
    replyToEmail,
    customerName,
    toEmails,
    ccEmails,
    bccEmails,
    emailSubject,
    emailMessage,
    payUrl,
    attachPdf,
    invoiceNumber,
    invoiceThumbnail,
    onDownloadInvoice,
    isDownloading,
    header,
}) => {
    const isReplyToValid = EMAIL_PATTERN.test(replyToEmail);

    const viewportRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const [viewportSize, setViewportSize] = useState<{
        width: number;
        height: number;
    } | null>(null);
    const [cardHeight, setCardHeight] = useState<number | null>(null);

    // transform: scale is invisible to layout measurements (unlike CSS zoom),
    // so observing the scaled card can't feed the scale back into itself.
    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        const card = cardRef.current;
        if (!viewport || !card) {
            return undefined;
        }
        const syncViewportSize = () => {
            // Zero measures happen mid-mount/animation; keep the last real one.
            if (viewport.clientWidth > 0 && viewport.clientHeight > 0) {
                setViewportSize({
                    width: viewport.clientWidth,
                    height: viewport.clientHeight,
                });
            }
        };
        const syncCardHeight = () => {
            if (card.offsetHeight > 0) {
                setCardHeight(card.offsetHeight);
            }
        };
        syncViewportSize();
        syncCardHeight();
        const viewportObserver = new ResizeObserver(syncViewportSize);
        const cardObserver = new ResizeObserver(syncCardHeight);
        viewportObserver.observe(viewport);
        cardObserver.observe(card);
        return () => {
            viewportObserver.disconnect();
            cardObserver.disconnect();
        };
    }, []);

    const headerAllowancePx = header ? HEADER_BAR_HEIGHT_PX : 0;
    // Never enlarged past its natural size (same rule as the invoice paper), so
    // the card's type stays at the declared 14px and its frame at 1px; it only
    // scales down to fit a narrow pane or the height left above the command bar
    // (the viewport is already inset from it, see .emailScaleViewport). The card
    // ends where its content ends — leftover height stays blank below it.
    const scale =
        viewportSize && cardHeight
            ? Math.max(
                  0,
                  Math.min(
                      1,
                      viewportSize.width / CARD_NATURAL_WIDTH_PX,
                      Math.max(0, viewportSize.height - headerAllowancePx) /
                          cardHeight,
                  ),
              )
            : 1;

    return (
        <div ref={viewportRef} className={styles.emailScaleViewport}>
            <div className={styles.emailCardGroup}>
                {header && (
                    <div className={styles.emailHeaderBar}>{header}</div>
                )}
                {/* Explicitly sized to the scaled card so the pane's layout
                    and centering see the visual size, not the pre-transform
                    box. */}
                <div
                    style={
                        cardHeight
                            ? {
                                  height: cardHeight * scale,
                                  width: CARD_NATURAL_WIDTH_PX * scale,
                              }
                            : undefined
                    }
                >
                    <div
                        ref={cardRef}
                        className={classNames(styles.emailPreviewCard, {
                            [styles.emailPreviewCardFlushTop]: Boolean(header),
                        })}
                        style={{ transform: `scale(${scale})` }}
                    >
                        <dl className={styles.emailPreviewMeta}>
                            <div className={styles.emailPreviewMetaRow}>
                                <dt className={styles.emailPreviewMetaLabel}>
                                    From
                                </dt>
                                <dd className={styles.emailPreviewMetaValue}>
                                    {fromName}
                                    <span
                                        className={
                                            styles.emailPreviewMetaAddress
                                        }
                                    >
                                        {wrapEmail(fromEmail)}
                                    </span>
                                </dd>
                            </div>
                            {(replyToName || replyToEmail) &&
                                isReplyToValid && (
                                    <div className={styles.emailPreviewMetaRow}>
                                        <dt
                                            className={
                                                styles.emailPreviewMetaLabel
                                            }
                                        >
                                            Reply to
                                        </dt>
                                        <dd
                                            className={
                                                styles.emailPreviewMetaValue
                                            }
                                        >
                                            {replyToName ? (
                                                <>
                                                    {replyToName}
                                                    <span
                                                        className={
                                                            styles.emailPreviewMetaAddress
                                                        }
                                                    >
                                                        {wrapEmail(
                                                            replyToEmail,
                                                        )}
                                                    </span>
                                                </>
                                            ) : (
                                                <span
                                                    className={
                                                        styles.emailPreviewMetaEmail
                                                    }
                                                >
                                                    {wrapEmail(replyToEmail)}
                                                </span>
                                            )}
                                        </dd>
                                    </div>
                                )}
                            <div className={styles.emailPreviewMetaRow}>
                                <dt className={styles.emailPreviewMetaLabel}>
                                    To
                                </dt>
                                <dd className={styles.emailPreviewMetaValue}>
                                    {customerName}
                                    {toEmails.length > 0 && (
                                        <span
                                            className={
                                                styles.emailPreviewMetaAddress
                                            }
                                        >
                                            {toEmails.map(wrapEmail).join(", ")}
                                        </span>
                                    )}
                                </dd>
                            </div>
                            {ccEmails.length > 0 && (
                                <div className={styles.emailPreviewMetaRow}>
                                    <dt
                                        className={styles.emailPreviewMetaLabel}
                                    >
                                        Cc
                                    </dt>
                                    <dd
                                        className={styles.emailPreviewMetaValue}
                                    >
                                        <span
                                            className={
                                                styles.emailPreviewMetaEmail
                                            }
                                        >
                                            {ccEmails.map(wrapEmail).join(", ")}
                                        </span>
                                    </dd>
                                </div>
                            )}
                            {bccEmails.length > 0 && (
                                <div className={styles.emailPreviewMetaRow}>
                                    <dt
                                        className={styles.emailPreviewMetaLabel}
                                    >
                                        Bcc
                                    </dt>
                                    <dd
                                        className={styles.emailPreviewMetaValue}
                                    >
                                        <span
                                            className={
                                                styles.emailPreviewMetaEmail
                                            }
                                        >
                                            {bccEmails
                                                .map(wrapEmail)
                                                .join(", ")}
                                        </span>
                                    </dd>
                                </div>
                            )}
                            <div className={styles.emailPreviewMetaRow}>
                                <dt className={styles.emailPreviewMetaLabel}>
                                    Subject
                                </dt>
                                <dd className={styles.emailPreviewMetaValue}>
                                    {emailSubject || "No subject"}
                                </dd>
                            </div>
                        </dl>

                        <div className={styles.emailPreviewBody}>
                            {emailMessage}
                        </div>

                        {payUrl ? (
                            <div className={styles.emailPreviewPay}>
                                <span className={styles.emailPreviewPayButton}>
                                    Pay invoice
                                </span>
                                <span className={styles.emailPreviewPayCopy}>
                                    Or copy this link:{" "}
                                    <span
                                        className={styles.emailPreviewPayLink}
                                    >
                                        {formatInvoicePayUrlDisplay(payUrl)}
                                    </span>
                                </span>
                            </div>
                        ) : null}

                        {attachPdf && (
                            <div className={styles.emailPreviewActions}>
                                <button
                                    type="button"
                                    className={styles.invoiceAttachment}
                                    onClick={onDownloadInvoice}
                                    disabled={isDownloading}
                                    aria-label={`Download invoice ${invoiceNumber} PDF`}
                                >
                                    <div
                                        className={
                                            styles.invoiceAttachmentThumb
                                        }
                                        data-invoice-flight-target=""
                                        aria-hidden="true"
                                    >
                                        {invoiceThumbnail}
                                    </div>
                                    <div
                                        className={styles.invoiceAttachmentMeta}
                                    >
                                        <span
                                            className={
                                                styles.invoiceAttachmentName
                                            }
                                        >
                                            {invoiceNumber
                                                ? `Invoice ${invoiceNumber}.pdf`
                                                : "Invoice.pdf"}
                                        </span>
                                        <span
                                            className={
                                                styles.invoiceAttachmentHint
                                            }
                                        >
                                            {isDownloading
                                                ? "Preparing…"
                                                : "PDF · Click to download"}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
